/**
 * The bridge between an authoritative room and the interface.
 *
 * Two screens sit on top of a `duel` room — the match and the practice range —
 * and both need the same two things: the coarse room state mirrored into React
 * without re-rendering on every 30 Hz tick, and the server's event stream turned
 * into scene effects and sound. Keeping one copy of each is what stops the
 * practice range from slowly drifting into a different game from the duel.
 */
import { useEffect } from 'react';
import type { RoomMode } from '@spell/shared-types';
import { net } from '../networking/net';
import { useNet, type PlayerMeta } from '../networking/netStore';
import { lastAttempt, useCastUi } from './castState';
import { kickShake, pushImpact, scene as bus, triggerHitStop } from '../scene/sceneBus';
import { sfx } from '../audio/sfx';

/* ------------------------------------------------------------------ */
/* Structural view of the synced schema                                */
/* ------------------------------------------------------------------ */

interface PlayerShape {
  sessionId: string;
  name: string;
  connected: boolean;
  ready: boolean;
  side: number;
  score: number;
  usingFallbackInput: boolean;
  micGranted: boolean;
  latencyMs: number;
  rematchReady: boolean;
  craftSubmitted: boolean;
}

interface RoomStateShape {
  mode: string;
  phase: string;
  round: number;
  phaseEndsAt: number;
  abortReason: string;
  projectileSpeedScale: number;
  practiceFire: boolean;
  practiceIntervalMs: number;
  players: { forEach(fn: (p: PlayerShape) => void): void };
}

function toMeta(p: PlayerShape): PlayerMeta {
  return {
    sessionId: p.sessionId,
    name: p.name,
    connected: p.connected,
    ready: p.ready,
    side: p.side,
    score: p.score,
    usingFallbackInput: p.usingFallbackInput,
    micGranted: p.micGranted,
    latencyMs: p.latencyMs,
    rematchReady: p.rematchReady,
    craftSubmitted: p.craftSubmitted,
  };
}

/* ------------------------------------------------------------------ */
/* Coarse state mirror                                                 */
/* ------------------------------------------------------------------ */

/**
 * Copy the room's human-visible fields into the React store, and only when one
 * of them actually changed. Health, mana and projectile positions never come
 * through here — the renderer and the HUD's animation frame read those straight
 * off `net.room.state`.
 */
export function useRoomStateMirror(): void {
  useEffect(() => {
    let lastSignature = '';
    const off = net.events.on('stateChange', () => {
      const room = net.room;
      if (!room) return;
      const state = room.state as unknown as RoomStateShape;
      const players: PlayerMeta[] = [];
      state.players.forEach((p) => players.push(toMeta(p)));

      const signature = [
        state.mode,
        state.phase,
        state.round,
        state.phaseEndsAt,
        state.abortReason,
        state.projectileSpeedScale,
        state.practiceFire,
        state.practiceIntervalMs,
        ...players.map(
          (p) =>
            `${p.sessionId}:${p.name}:${p.score}:${p.ready}:${p.connected}:${p.usingFallbackInput}:${p.rematchReady}:${p.craftSubmitted}`,
        ),
      ].join('|');
      if (signature === lastSignature) return;
      lastSignature = signature;

      useNet.getState().set({
        mode: (state.mode === 'practice' ? 'practice' : 'duel') satisfies RoomMode,
        phase: state.phase,
        round: state.round,
        phaseEndsAt: state.phaseEndsAt,
        abortReason: state.abortReason,
        players,
        speedScale: state.projectileSpeedScale,
        practice: {
          fire: state.practiceFire,
          intervalMs: state.practiceIntervalMs,
        },
      });
    });
    return off;
  }, []);
}

/* ------------------------------------------------------------------ */
/* Server events                                                       */
/* ------------------------------------------------------------------ */

export interface ServerEventOptions {
  /** Fired after a granted spell lands, so a craft screen can stop spinning. */
  onSpellGranted?: () => void;
}

/**
 * Turn the server's event stream into scene effects, sound and store updates.
 *
 * Every judgement here has already been made by the room; nothing in this file
 * decides an outcome, it only reacts to one.
 */
export function useServerEvents({ onSpellGranted }: ServerEventOptions = {}): void {
  useEffect(() => {
    const offs = [
      // The server's authoritative list — it also delivers the shared bolt,
      // which is never crafted and therefore never "granted".
      net.events.on('spellBook', (msg) => {
        useNet.getState().setSpells(msg.spells);
      }),

      net.events.on('spellGranted', (msg) => {
        useNet.getState().addSpell(msg.spell);
        useNet.getState().set({ lastCastNotes: msg.notes, craft: null });
        onSpellGranted?.();
        sfx.circle();
      }),

      net.events.on('opponentSpell', (msg) => {
        useNet.getState().set({
          opponentSpells: [
            ...useNet.getState().opponentSpells.filter((s) => s.id !== msg.id),
            msg,
          ],
        });
      }),

      net.events.on('craftStart', (msg) => {
        useNet.getState().set({ craft: { open: true, slot: msg.slot, endsAt: msg.endsAt, round: msg.round } });
        document.exitPointerLock?.();
      }),

      net.events.on('castResult', (msg) => {
        useCastUi.getState().setLastResult({
          grade: msg.grade,
          gesture: msg.gestureScore,
          chant: msg.chantScore,
          reason: msg.reason,
          parry: false,
          timingError: 0,
          at: Date.now(),
          heard: lastAttempt.heard,
          expected: lastAttempt.expected,
        });
        if (msg.grade === 'Fail') sfx.fail();
      }),

      net.events.on('parryResult', (msg) => {
        useCastUi.getState().setLastResult({
          grade: msg.grade,
          gesture: 0,
          chant: 0,
          reason: msg.reason,
          parry: true,
          timingError: msg.timingError,
          at: Date.now(),
          heard: lastAttempt.heard,
          expected: lastAttempt.expected,
        });
        if (msg.success) {
          // The core beat of the game: freeze, then a short slow-motion tail.
          bus.circle.burstAt = performance.now();
          triggerHitStop(0.1, 0.45, 0.3);
          kickShake(1.1);
          sfx.parry(msg.grade === 'Fail' ? 'Weak' : (msg.grade as 'Weak' | 'Normal' | 'Perfect'));
        } else {
          sfx.fail();
        }
      }),

      net.events.on('impact', (msg) => {
        pushImpact({
          x: msg.x,
          y: msg.y,
          z: msg.z,
          color: msg.color,
          kind: msg.kind,
          amount: msg.amount,
          life: msg.kind === 'dot' ? 0.35 : 0.75,
          self: msg.targetId === net.sessionId,
        });
        if (msg.targetId === net.sessionId && msg.kind === 'damage') {
          kickShake(Math.min(1.2, 0.25 + msg.amount / 30));
          sfx.impact(Math.min(1.4, 0.5 + msg.amount / 26));
        } else if (msg.kind === 'damage') {
          sfx.impact(0.5);
        }
        if (msg.lethal) triggerHitStop(0.16, 0.9, 0.25);
      }),

      net.events.on('roundResult', (msg) => {
        useNet.getState().set({
          announcement: {
            kind: 'roundEnd',
            text: msg.draw ? '무승부' : msg.winnerId === net.sessionId ? '라운드 승리' : '라운드 패배',
            at: Date.now(),
          },
        });
        document.exitPointerLock?.();
      }),

      net.events.on('matchResult', (msg) => {
        useNet.getState().set({ matchResult: msg, craft: null });
        document.exitPointerLock?.();
      }),

      net.events.on('announce', (msg) => {
        useNet.getState().set({ announcement: { ...msg, at: Date.now() } });
        if (msg.kind === 'countdown') sfx.countdown();
        if (msg.kind === 'roundStart') sfx.countdown(true);
        window.setTimeout(() => {
          const current = useNet.getState().announcement;
          if (current && current.text === msg.text) useNet.getState().set({ announcement: null });
        }, 2400);
      }),

      net.events.on('protocolError', (msg) => {
        if (msg.fatal) useNet.getState().set({ fatalError: msg.message });
      }),

      net.events.on('disconnected', () => {
        useNet.getState().set({ status: 'closed' });
      }),
    ];
    return () => offs.forEach((off) => off());
    // The handler set is installed once for the lifetime of the screen; the
    // callback is read through the closure captured at mount, which is stable
    // because both callers pass a `useCallback`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
